import React, { Suspense, useEffect } from 'react';
import '@/globalStyles/main.scss';
import './globalStyles/positions-chart.scss';
import './i18n';
import { RouterProvider } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import { darkTheme, lightTheme } from './globalStyles/theme';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { selectTheme } from './state/general/generalSelectors';
import InitialLoading from './components/InitialLoading';
import { THEME_VARIANTS } from './state/member/memberTypes';
import CrashSite from './components/ErrorBoundary/CrashSite';
import useAppRouter from '@/router';
import HooksConnectComponent from '@/components/HooksConnectComponent';
import useFirebaseListeners from '@/hooks/useFirebaseListeners';
import useSmartlook from './hooks/useSmartlook';
import useTinybaseListeners from '@/store/syncStore/hooks/useTinybaseListeners';
import * as Sentry from '@sentry/react';
import DataThemeSync from '@/components/DataThemeSync';
import QueryProvider from '@/lib/query/QueryProvider';
import { selectMemberProfile } from '@/state/member/memberSelectors';
import { setSentryUser } from '@/lib/sentry/sentry';

function App() {
    const storedTheme = useSelector((state: RootState) => selectTheme(state));
    const { id: memberId } = useSelector((state: RootState) => selectMemberProfile(state));

    const router = useAppRouter();
    useFirebaseListeners();
    useSmartlook();
    useTinybaseListeners();

    useEffect(() => {
        const root = document.documentElement;
        const color =
            storedTheme === THEME_VARIANTS.LIGHT
                ? lightTheme.palette.text.primary
                : darkTheme.palette.text.primary;
        root.style.setProperty('--text-primary', color);
    }, [storedTheme]);

    useEffect(() => {
        setSentryUser(memberId ?? null);
    }, [memberId]);

    return (
        <QueryProvider>
            <ThemeProvider theme={storedTheme === THEME_VARIANTS.LIGHT ? lightTheme : darkTheme}>
                <CssBaseline />
                <DataThemeSync />
                <Suspense fallback={<InitialLoading />}>
                    <HooksConnectComponent />
                    <Sentry.ErrorBoundary fallback={<CrashSite />}>
                        <RouterProvider router={router} />
                    </Sentry.ErrorBoundary>
                </Suspense>
            </ThemeProvider>
        </QueryProvider>
    );
}

export default App;
